import type { Address } from '~/types/location'
import * as Sentry from '@sentry/vue'
import { trackServerSide } from '~/server/utils/tracking'
import { useDateTimeStore } from '~/stores/datetime'
import { useLocationStore } from '~/stores/location'

export interface AddressSearchTrackingData {
  address: string
  latitude: number
  longitude: number
  startDateTime: string
  endDateTime: string
  isBookable: boolean
  sessionId: string
  locationId: number
}

export function usePrebookingTracking() {
  const sessionId = ref<string>()

  async function createAnonymousSessionId(): Promise<string> {
    const timestamp = Date.now()
    const randomValue = Math.random()
    const config = useRuntimeConfig()
    const sessionData = `${timestamp}-${randomValue}-${config.public.SENTRY_ENV || 'unknown'}`

    // Use crypto.subtle if available (both server and modern browsers)
    if (typeof crypto !== 'undefined' && crypto.subtle) {
      const msgBuffer = new TextEncoder().encode(sessionData)
      const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer)
      const hashArray = Array.from(new Uint8Array(hashBuffer))
      const hashHex = hashArray.map(b => (`00${b.toString(16)}`).slice(-2)).join('')
      return hashHex.substring(0, 36)
    }

    // fallback if not available
    return `session-${timestamp}-${Math.random().toString(36).substring(2, 15)}`
  }

  async function getSessionId(): Promise<string> {
    if (!sessionId.value) {
      sessionId.value = await createAnonymousSessionId()
    }
    return sessionId.value
  }

  /**
   * Tracks valid address searches via server-to-server calls
   * Works universally (server and client) but requests are invisible to browser console
   */
  const trackValidAddressSearch = async (): Promise<void> => {
    try {
      const locationStore = useLocationStore()
      const dateTimeStore = useDateTimeStore()

      // Only track if we have valid address with location data
      if (!locationStore.address?.location) {
        if (import.meta.server) {
          console.warn('Tracking: no valid address location data')
        }
        return
      }

      const trackingData: AddressSearchTrackingData = {
        address: locationStore.address.info,
        latitude: locationStore.address.location.latitude,
        longitude: locationStore.address.location.longitude,
        startDateTime: dateTimeStore.startDate.date?.toISOString() || '',
        endDateTime: dateTimeStore.endDate.date?.toISOString() || '',
        isBookable: !locationStore.addressError, // inZone from validation
        sessionId: await getSessionId(),
        locationId: locationStore.city.locationId,
      }

      await trackServerSide('api/rental/prebooking-service/track/address-search', trackingData)
    }
    catch (error: unknown) {
      Sentry.captureException(error)
    }
  }

  /**
   * Tracks out-of-business-area address searches via server-to-server calls
   * Works universally (server and client) but requests are invisible to browser console
   */
  const trackOutOfBusinessAreaAddressSearch = async (address: Address | undefined): Promise<void> => {
    try {
      const locationStore = useLocationStore()
      // Prepare tracking data
      const trackingData: Partial<AddressSearchTrackingData> = {
        address: address?.info,
        latitude: address?.location?.latitude,
        longitude: address?.location?.longitude,
        isBookable: false, // cannot be true if location check marks as not inZone
        sessionId: await getSessionId(),
        locationId: locationStore.city.locationId,
      }

      await trackServerSide('api/rental/prebooking-service/track/out-of-business-area-search', trackingData)
    }
    catch (error: unknown) {
      Sentry.captureException(error)
    }
  }

  return {
    trackValidAddressSearch,
    trackOutOfBusinessAreaAddressSearch,
  }
}
